import loadCustomCountries from "@bevco/Store/Handlers/Utilities/CustomLoadCountries";
import { CustomState } from "@bevco/Store/Reducers";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setFieldValue from "@insite/client-framework/Store/Components/ContactUsForm/Handlers/SetFieldValue";
import {
    getCountriesDataView,
    getCurrentCountries,
} from "@insite/client-framework/Store/Data/Countries/CountriesSelectors";
import { loadCurrentCountriesParameter } from "@insite/client-framework/Store/Data/Countries/Handlers/LoadCurrentCountries";
import { CountryModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ContactFormContext } from "@insite/content-library/Widgets/ContactUs/ContactForm";
import LoadingOverlay from "@insite/mobius/LoadingOverlay";
import Select, { SelectPresentationProps } from "@insite/mobius/Select";
import * as React from "react";
import { useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";

const enum fields {
    label = "label",
    hintText = "hintText",
    isRequired = "isRequired",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.label]: string;
        [fields.hintText]: string;
        [fields.isRequired]: boolean;
    };
}

const mapStateToProps = (state: CustomState) => {
    const fieldName = "country";

    return {
        fieldName,
        fieldValue: state.components.contactUsForm.fieldValues[fieldName],
        countries: state.customCountriesList.countries as CountryModel[],
    };
};

const mapDispatchToProps = {
    setFieldValue,
    loadCustomCountries,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface FormFieldStyles {
    selectField?: SelectPresentationProps;
}

export const formFieldStyles: FormFieldStyles = {};

const styles = formFieldStyles;

const Company: React.FC<Props> = ({ fields, fieldName, fieldValue, setFieldValue, countries, loadCustomCountries }) => {
    const { label, hintText, isRequired } = fields;
    const contactFormContext = React.useContext(ContactFormContext);
    const [fieldErrorMessage, setFieldErrorMessage] = React.useState("");

    React.useEffect(() => {
        contactFormContext.validators[fieldName] = validateField;
        if (typeof fieldValue !== "undefined") {
            validateField();
        }
    }, [fieldValue]);

    React.useEffect(() => {
        contactFormContext.validators[fieldName] = validateField;
    }, [isRequired]);

    useEffect(() => {
        loadCustomCountries({});
    }, []);

    const validateField = () => {
        const errorMessage = isRequired && !fieldValue ? (siteMessage("Field_Required", label) as string) : "";
        setFieldErrorMessage(errorMessage);
        return !errorMessage;
    };

    const fieldChangeHandler = (event: React.ChangeEvent<HTMLSelectElement>) => {
        setFieldValue({ key: fieldName, value: event.target.value });
    };

    return (
        <LoadingOverlay style={{ width: "100%" }} loading={countries.length === 0}>
            <Select
                {...styles.selectField}
                label={label}
                required={isRequired}
                value={fieldValue || ""}
                error={fieldErrorMessage}
                onChange={fieldChangeHandler}
            >
                <option value="">{hintText}</option>
                {countries &&
                    countries.map(country => (
                        <option key={country.id} value={country.name}>
                            {country.name}
                        </option>
                    ))}
            </Select>
        </LoadingOverlay>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(Company as any) as any,
    definition: {
        group: "Contact Us",
        icon: "input-pipe",
        fieldDefinitions: [
            {
                name: fields.label,
                displayName: "Label",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "Country",
                isRequired: true,
            },
            {
                name: fields.hintText,
                displayName: "Hint Text",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "Select",
                isRequired: true,
            },
            {
                name: fields.isRequired,
                displayName: "Required Field",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
